import { normalizarGraus, sentidoAFavorDoVento } from '../vento'
import {
  adicionarObjeto,
  atualizarObjeto,
  definirMeteorologia,
  meteorologiaDaCena,
} from './operacoes'
import type { CenaTabletop, GeradorIdTabletop, SituacaoFonte } from './tipos'
import { VAZAO_PADRAO_KG_S } from './camadas'

/**
 * Injeções do roteiro da mesa: o que o instrutor "solta" durante a aula para
 * mudar a ocorrência — o vento gira, anoitece, o vazamento aumenta, o fogo
 * alcança o tanque, uma vítima aparece. Cada uma é uma transformação pura da
 * cena; as camadas calculadas reagem sozinhas.
 */

export type EfeitoInjecao =
  | { tipo: 'girarVento'; deltaGraus: number; ventoMS?: number }
  | { tipo: 'mudarPeriodo'; periodo: 'dia' | 'noite' }
  | { tipo: 'situacaoDasFontes'; situacao: SituacaoFonte }
  | { tipo: 'multiplicarVazao'; fator: number }
  | { tipo: 'vitimaAFavorDoVento'; distanciaM: number }

export interface Injecao {
  id: string
  titulo: string
  /** Texto lido para a guarnição quando a injeção entra. */
  mensagem: string
  efeito: EfeitoInjecao
}

export const INJECOES_PADRAO: readonly Injecao[] = [
  {
    id: 'vento-gira',
    titulo: 'O vento muda de direção',
    mensagem:
      'Central informa: o vento girou 90° e reforçou. A biruta mostra a nova direção.',
    efeito: { tipo: 'girarVento', deltaGraus: 90, ventoMS: 5 },
  },
  {
    id: 'anoitece',
    titulo: 'Anoitece',
    mensagem:
      'O sol se pôs e o céu está limpo. A atmosfera fica estável: a pluma estreita e vai mais longe.',
    efeito: { tipo: 'mudarPeriodo', periodo: 'noite' },
  },
  {
    id: 'vazamento-aumenta',
    titulo: 'O vazamento aumenta',
    mensagem:
      'A equipe de reconhecimento informa que o furo aumentou: a vazão triplicou.',
    efeito: { tipo: 'multiplicarVazao', fator: 3 },
  },
  {
    id: 'vazamento-comeca',
    titulo: 'Começa um vazamento',
    mensagem:
      'Reconhecimento: vazamento visível na válvula do tanque, com névoa junto ao chão.',
    efeito: { tipo: 'situacaoDasFontes', situacao: 'vazamento' },
  },
  {
    id: 'fogo-tanque',
    titulo: 'O fogo atinge o tanque',
    mensagem:
      'Chamas envolvem o tanque e a válvula de alívio começou a abrir com ruído crescente.',
    efeito: { tipo: 'situacaoDasFontes', situacao: 'tanqueEmFogo' },
  },
  {
    id: 'vitima',
    titulo: 'Uma vítima aparece',
    mensagem:
      'Morador relata uma pessoa caída a favor do vento, a uns 150 m do caminhão.',
    efeito: { tipo: 'vitimaAFavorDoVento', distanciaM: 150 },
  },
]

export function aplicarInjecao(
  cena: CenaTabletop,
  injecao: Injecao,
  gerarId?: GeradorIdTabletop,
): CenaTabletop {
  const { efeito } = injecao
  const meteorologia = meteorologiaDaCena(cena)
  const fontes = cena.objetos.filter((o) => o.fonte)

  switch (efeito.tipo) {
    case 'girarVento':
      return definirMeteorologia(cena, {
        direcaoVentoGraus: normalizarGraus(
          meteorologia.direcaoVentoGraus + efeito.deltaGraus,
        ),
        ...(efeito.ventoMS === undefined ? {} : { ventoMS: efeito.ventoMS }),
      })
    case 'mudarPeriodo':
      return definirMeteorologia(cena, {
        periodo: efeito.periodo,
        ceuEncoberto: false,
      })
    case 'situacaoDasFontes':
      return fontes.reduce(
        (atual, objeto) =>
          atualizarObjeto(atual, objeto.id, {
            fonte: { situacao: efeito.situacao },
          }),
        cena,
      )
    case 'multiplicarVazao':
      return fontes.reduce(
        (atual, objeto) =>
          atualizarObjeto(atual, objeto.id, {
            fonte: {
              situacao: 'vazamento',
              vazaoKgS:
                (objeto.fonte!.vazaoKgS ?? VAZAO_PADRAO_KG_S) * efeito.fator,
            },
          }),
        cena,
      )
    case 'vitimaAFavorDoVento': {
      const referencia = fontes[0]
      const [x0, , z0] = referencia?.transformacao.posicao ?? [0, 0, 0]
      const [dx, dz] = sentidoAFavorDoVento(meteorologia.direcaoVentoGraus)
      return adicionarObjeto(
        cena,
        {
          tipo: 'vitima',
          nome: 'Vítima relatada',
          transformacao: {
            posicao: [
              x0 + dx * efeito.distanciaM,
              0,
              z0 + dz * efeito.distanciaM,
            ],
            rotacao: [0, 0, 0],
            escala: [1, 1, 1],
          },
        },
        gerarId,
      )
    }
  }
}
